import { useContext } from "react";
import { useIgPrices } from "@intelligems/headless/gatsby";

import { GeoContext } from "../context/GeoProvider";

import {
  formatPriceAmount,
  useCurrentCurrencyCode,
  useCurrentCountryCode,
} from "../hooks/usePrices";
import { roundNumber } from "./general";

function processIgPrice(igPrices, prices, countryCode) {
  if (
    igPrices &&
    igPrices.isIgPrice &&
    igPrices.isReady &&
    igPrices.igCompareAtPrice &&
    igPrices.igPrice
  ) {
    const fullPrice = igPrices.igCompareAtPrice.value || igPrices.igPrice.value;
    const finalPrice = igPrices.igPrice.value;
    return {
      isIgPrice: true,
      compareAtPrice: igPrices.igCompareAtPrice.value,
      fullPrice,
      finalPrice,
      formattedDiscount: formatPriceAmount(
        fullPrice - finalPrice,
        igPrices.igPrice.currencyCode,
        countryCode
      ),
      discountAmount: fullPrice - finalPrice,
      hasDiscount: igPrices.igCompareAtPrice.value - igPrices.igPrice.value > 0,
      discountGiven: igPrices.igCompareAtPrice.value
        ? roundNumber(
            ((igPrices.igCompareAtPrice.value - igPrices.igPrice.value) /
              igPrices.igCompareAtPrice.value) *
              100
          )
        : 0,
    };
  }

  return {
    compareAtPrice: prices?.compareAtPrice,
    hasDiscount: (prices?.full_price || 0) - (prices?.final_price || 0) > 0,
    discountGiven: prices?.discount_given,
    finalPrice: prices?.finalPrice || prices?.final_price,
    fullPrice: prices?.fullPrice || prices?.full_price,
    formattedDiscount: prices?.formattedDiscount,
    discountAmount: prices?.discountAmount,
  };
}

export function applyIgPrices(productId, selectedVariant, prices) {
  const { gePriceDetails } = useContext(GeoContext);

  const igPrices = useIgPrices({
    productId,
    variantId: selectedVariant?.id,
    originalPrice: prices?.final_price || prices?.full_price,
    originalCompareAtPrice: prices?.compareAtPrice,
    currencyCode: gePriceDetails?.CurrencyCode,
  });

  // Use intelligems prices if they are available, recalculate discount if so
  // otherwise use the prices usePriceInfo prices
  return processIgPrice(igPrices, prices, gePriceDetails?.CountryCode);
}

/**
 * Apply Intelligems prices on variants
 * @description Same as applyIgPrices but for multiple variants, returns an object with variantId as key and the price object as value
 * @param {string} productId
 * @param {Array} variants
 * @returns {Object} - { [variantId]: priceObject }
 */
export function useApplyIgPricesOnVariants(productId, variants) {
  const currencyCode = useCurrentCurrencyCode();
  const countryCode = useCurrentCountryCode();
  const igPricesByVariant = useIgPrices(
    variants?.map((variant) => ({
      productId,
      variantId: variant.id,
      originalPrice: variant?.final_price || variant?.full_price || "10",
      originalCompareAtPrice: variant?.compareAtPrice,
      currencyCode,
    }))
  );

  const pricesWithIg = {};
  variants?.forEach((variant) => {
    const variantId = variant.id;
    const variantIgPrices = igPricesByVariant[variantId];
    pricesWithIg[variantId] = processIgPrice(variantIgPrices, variant, countryCode);
  });

  return pricesWithIg;
}

/**
 * Apply Intelligems prices on multiple products with different productIds
 * @param {Array} products - Array of { productId, variant, prices }
 * @returns {Object} - { [productId]: priceObject }
 */
export function useApplyIgPricesOnProducts(products) {
  const currencyCode = useCurrentCurrencyCode();
  const countryCode = useCurrentCountryCode();

  const igPricesByVariant = useIgPrices(
    products?.map(({ productId, variant, prices }) => ({
      productId,
      variantId: variant?.id,
      originalPrice: prices?.final_price || prices?.full_price,
      originalCompareAtPrice: prices?.compareAtPrice,
      currencyCode,
    }))
  );

  const result = {};
  products?.forEach(({ productId, variant, prices }) => {
    result[productId] = processIgPrice(igPricesByVariant?.[variant?.id], prices, countryCode);
  });

  return result;
}
